import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { of, Subject } from "rxjs";
import { FormBuilder, FormGroup } from "@angular/forms";
import { delay, skip, take, takeUntil } from "rxjs/operators";
import { MatButtonToggleChange } from "@angular/material/button-toggle";
import {
    availablePeriods,
    RangePickerComponentStore,
    RangePickerPeriod,
    RangePickerState,
} from "./range-picker-component.store";
import { DateRange, MatCalendar } from "@angular/material/datepicker";
import { endOfMonth, endOfYear, startOfMonth, startOfYear } from "date-fns";

@Component({
    selector: "stats-range-picker",
    templateUrl: "./range-picker.component.html",
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [RangePickerComponentStore],
})
export class RangePickerComponent implements OnInit, OnDestroy {
    private _unsubscribeAll = new Subject();
    private _store = inject(RangePickerComponentStore);
    form: FormGroup;

    @Input()
    initialState: RangePickerState;

    @Output()
    onFilter = new EventEmitter<RangePickerState>();

    @Output()
    onClose = new EventEmitter<boolean>();

    datasource$ = this._store.datasource$;

    period = this._store.period$;

    availablePeriods = availablePeriods;

    selectedRangeValue: DateRange<Date> | undefined;

    @ViewChild(MatCalendar, { static: false }) calendar: MatCalendar<Date>;

    constructor(private _changeDetector: ChangeDetectorRef, private _formBuilder: FormBuilder) {
        this.form = this._formBuilder.group({
            period: [],
            periodStart: [],
            periodEnd: [],
        });
    }

    ngOnInit(): void {
        this.datasource$
            .pipe(takeUntil(this._unsubscribeAll))
            .pipe(skip(1))
            .subscribe((data) => {
                this.form.patchValue(data);
                this.selectedRangeValue = new DateRange<Date>(data.periodStart, data.periodEnd);
                this._changeDetector.detectChanges();
            });
        this._store.init(this.initialState);
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    selectPeriod(e: MatButtonToggleChange) {
        this._store.selectPeriod(e.value);
        this._changeDetector.detectChanges();
    }

    selectedChange(m: any) {
        let period = this.form.getRawValue().period;
        //Daily
        if (period === 1) {
            this.selectedRangeValue = new DateRange<Date>(m, m);
            this._store.changeDate(m, m);
            this._changeDetector.detectChanges();
        } else if (!this.selectedRangeValue?.start || this.selectedRangeValue?.end) {
            this.selectedRangeValue = new DateRange<Date>(m, null);
            this._changeDetector.detectChanges();
        } else {
            let start = this.selectedRangeValue.start;
            let end = m;
            if (end < start) {
                start = m;
                end = this.selectedRangeValue.start;
            }
            this.selectedRangeValue = new DateRange<Date>(start, end);
            this._store.changeDate(start, end);
        }
    }

    yearSelected(m) {
        this.period.pipe(take(1)).subscribe((period) => {
            if (period === RangePickerPeriod.yearly) {
                let start = startOfYear(m);
                let end = endOfYear(m);
                this.selectedRangeValue = new DateRange<Date>(start, end);
                this._store.changeDate(start, end);
                of(true)
                    .pipe(delay(50))
                    .subscribe(() => {
                        this.calendar.currentView = "month";
                    });
            } else {
                this.selectedRangeValue = new DateRange<Date>(m, null);
            }
            this._changeDetector.detectChanges();
        });
    }

    monthSelected(m) {
        this.period.pipe(take(1)).subscribe((period) => {
            if (period === RangePickerPeriod.monthly) {
                let start = startOfMonth(m);
                let end = endOfMonth(m);
                this.selectedRangeValue = new DateRange<Date>(start, end);
                this._store.changeDate(start, end);
            } else {
                this.selectedRangeValue = new DateRange<Date>(m, null);
            }
            this._changeDetector.detectChanges();
        });
    }

    close() {
        this.onClose.emit(true);
    }

    save() {
        const state: RangePickerState = {
            period: this.form.value.period,
            periodStart: this.selectedRangeValue.start!,
            periodEnd: this.selectedRangeValue.end ?? this.selectedRangeValue.start!,
        };
        this.onFilter.emit(state);
        this._store.changeDate(state.periodStart, state.periodEnd);
    }
}
